import { Button } from '@/components/ui/button';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { useCart } from '../hooks/useCart';
import { useCollection } from '../hooks/useCollection';
import { useTranslation } from '../hooks/useTranslation';
import { useUserStore } from '../hooks/useUser';
import PokemonAddToCollectionButton from './PokemonAddToCollectionButton';

export default function PokemonCard({ card, showBuyButton = true }) {
  const { addCardToCart, updateQuantity, getQuantityOfItem } = useCart();
  const { user } = useUserStore();
  const { getQuantityInCollection } = useCollection();
  const { t } = useTranslation();
  const cardId = card?.id;

  // Extract card data
  const cardName = card?.name || '';
  const setName = card?.set_name || '';
  const imageSrc = card?.image_uris?.large || card?.image_uris?.small || '';
  const price = card?.prices?.usd_holofoil || card?.prices?.usd || null;
  const formattedPrice = price ? `$${price.toFixed(2)}` : '';

  // Get card quantity in cart and collection
  const quantity = getQuantityOfItem(cardId);
  const collectionQuantity = getQuantityInCollection(cardId);

  const formattedUrl = () => {
    try {
      if (!card?.tcgplayer?.url) return null;
      const partnerUrl = `https://partner.tcgplayer.com/tcgstacked`;
      return `${partnerUrl}?u=${encodeURIComponent(card.tcgplayer.url)}`;
    } catch (error) {
      console.error('Error formatting TCGPlayer URL:', error);
      return null;
    }
  };

  const theFormattedUrl = formattedUrl();

  const increment = () => {
    if (quantity === 0) {
      addCardToCart({
        ...card,
        id: card.id,
        tcg: 'pokemon',
      });
    } else {
      updateQuantity(cardId, 1);
    }
  };

  return (
    <div className='space-y-2'>
      <Link className='flex flex-col gap-1' href={`/pokemon/card/${cardId}`}>
        <div className='relative'>
          {imageSrc ? (
            <Image src={imageSrc} alt={cardName} width={245} height={342} className='rounded-lg object-contain w-full h-auto' />
          ) : (
            <div className='aspect-[245/342] bg-gray-200 dark:bg-gray-800 rounded-lg flex items-center justify-center'>
              <span className='text-gray-500 dark:text-gray-400'>No image</span>
            </div>
          )}

          {collectionQuantity > 0 && <div className='absolute top-2 right-2 bg-emerald-600/90 dark:bg-emerald-500/90 text-white px-2 py-0.5 rounded-md text-xs font-medium shadow-sm'>{collectionQuantity}</div>}
        </div>

        <div className='flex flex-col'>
          <h3 className='text-base truncate'>{cardName}</h3>
          <div className='flex justify-between items-center'>
            <p className='text-sm text-gray-600 dark:text-gray-400 truncate'>{setName}</p>
            {formattedPrice && (
              <div className='flex items-center gap-1'>
                <Image src='/tcgplayer.webp' alt='TCGplayer' width={12} height={12} className='object-contain' />
                <p className='text-sm font-medium'>{formattedPrice}</p>
              </div>
            )}
          </div>
        </div>
      </Link>

      <div className='mt-3 w-full flex flex-col gap-2'>
        <div className='flex gap-2 flex-col'>
          {/* Primary action - Add to Print List / Quantity selector */}
          <div className='flex-1'>
            {quantity > 0 ? (
              <select
                value={quantity}
                onChange={(e) => updateQuantity(cardId, parseInt(e.target.value) - quantity)}
                className='w-full border-0 rounded-md bg-gray-800 text-white hover:bg-gray-900 focus:outline-none text-center text-sm font-medium dark:bg-gray-50 dark:text-gray-900'
              >
                <option value={0}>{t('card.removeFromPrintList')}</option>
                {Array.from({ length: 100 }, (_, i) => i + 1).map((num) => (
                  <option key={num} value={num}>
                    {num} {num === 1 ? t('card.cardsInPrintList') : t('card.cardsInPrintListPlural')}
                  </option>
                ))}
              </select>
            ) : (
              <Button variant='primary' onClick={increment} className='w-full'>
                {t('card.addToPrintList')}
              </Button>
            )}
          </div>

          {user && <PokemonAddToCollectionButton card={card} asDropdownItem={false} />}

          {theFormattedUrl && (
            <Button variant='outline' asChild className='flex-1'>
              <Link href={theFormattedUrl} target='_blank' className='flex items-center'>
                <Image src='/tcgplayer.webp' alt='TCGplayer' width={16} height={16} className='mr-2 object-contain' />
                {formattedPrice ? `${t('card.buyFor')}${formattedPrice}` : t('card.buyOn')}
              </Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
